import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Progress,
  Typography,
  Space,
  Spin,
  Statistic,
  Row,
  Col,
  Descriptions,
  message,
  Table,
  Tabs,
  Badge,
  InputNumber,
  Select,
} from 'antd';
import {
  ArrowLeftOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  FieldTimeOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

interface CaptioningTask {
  id: number;
  name: string;
  dataset_id: number;
  dataset_name: string;
  prompt_id: number;
  prompt_name: string;
  prompt_content: string;
  prompt_version: number;
  model: string;
  provider: string;
  batch_size: number;
  max_concurrent: number;
  requests_per_second: number;
  status: string;
  total_images: number;
  pending_count: number;
  success_count: number;
  failed_count: number;
  size_filter: string[] | null;
  aspect_ratio_filter: string[] | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface CaptionResult {
  id: number;
  image_id: number;
  status: string;
  json_file_path: string | null;
  error_message: string | null;
  s3_url: string;
  thumbnail_uri: string;
  bucket_name: string;
  region: string;
  processed_at: string | null;
}

interface SpeedSample {
  timestamp: number;
  processed: number;
}

// Constants for performance targets
const TARGET_SPEED = 100; // 100 img/s target
const MAX_THEORETICAL_SPEED = 112; // 14 keys * 8 req/s

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const CaptioningTaskView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<CaptioningTask | null>(null);
  const [results, setResults] = useState<CaptionResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsPagination, setResultsPagination] = useState({ page: 1, total: 0, pageSize: 20 });
  const [resultsFilter, setResultsFilter] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [speed, setSpeed] = useState<number>(0);
  const [instantSpeed, setInstantSpeed] = useState<number>(0);
  const [eta, setEta] = useState<string>('-');
  const [editingBatchSize, setEditingBatchSize] = useState(false);
  const [editingRequestsPerSecond, setEditingRequestsPerSecond] = useState(false);
  const [editingFilters, setEditingFilters] = useState(false);
  const [tempBatchSize, setTempBatchSize] = useState<number>(0);
  const [tempRequestsPerSecond, setTempRequestsPerSecond] = useState<number>(6);
  const [tempSizeFilter, setTempSizeFilter] = useState<string[]>([]);
  const [tempAspectRatioFilter, setTempAspectRatioFilter] = useState<string[]>([]);
  const [sizeOptions, setSizeOptions] = useState<{label: string, value: string}[]>([]);
  const [aspectRatioOptions, setAspectRatioOptions] = useState<{label: string, value: string}[]>([]);
  const [saving, setSaving] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const speedSamplesRef = useRef<SpeedSample[]>([]);

  useEffect(() => {
    fetchTask();
    fetchResults();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [id]);

  // WebSocket connection for real-time updates
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!id) return;

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${process.env.REACT_APP_WS_HOST || 'localhost:3003'}/ws`;
      console.log('[WebSocket] Connecting to:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] Connected for captioning task:', id);
        reconnectAttempts = 0;
        const subscribeMsg = { action: 'subscribe', channel: `captioning-task:${id}` };
        console.log('[WebSocket] Subscribing:', subscribeMsg);
        ws.send(JSON.stringify(subscribeMsg));
      };

      ws.onmessage = (event) => {
        console.log('[WebSocket] Raw message:', event.data);
        try {
          const parsed = JSON.parse(event.data);
          console.log('[WebSocket] Parsed:', parsed);

          const { channel, data, type } = parsed;

          // Initial connection message
          if (type === 'connected') {
            console.log('[WebSocket] Connection confirmed');
            return;
          }

          // Progress update from Redis
          if (data?.type === 'caption-progress') {
            console.log('[WebSocket] Progress update:', data);
            setTask(prev => prev ? {
              ...prev,
              success_count: data.successCount,
              failed_count: data.failedCount,
              pending_count: data.pendingCount
              // Don't update status from worker - it doesn't know the real status
            } : prev);
          }

          // Task status change (completion, etc.)
          if (data?.type === 'task-status') {
            console.log('[WebSocket] Status update:', data);
            setTask(prev => prev ? {
              ...prev,
              status: data.status
            } : prev);
            // Refresh full task data on status change
            fetchTask();
          }
        } catch (err) {
          console.error('[WebSocket] Parse error:', err);
        }
      };

      ws.onerror = (err) => {
        console.error('[WebSocket] Error:', err);
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected, code:', event.code, 'reason:', event.reason);
        wsRef.current = null;

        // Attempt reconnection if not a clean close
        if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
          console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'unsubscribe', channel: `captioning-task:${id}` }));
        }
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, [id]);

  useEffect(() => {
    // Setup polling for running tasks
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (task?.status === 'running') {
      intervalRef.current = setInterval(() => {
        fetchTask();
        fetchResults(resultsPagination.page, undefined, false); // Keep current page/filter, no loading spinner
      }, 5000); // Poll every 5 seconds as fallback (WebSocket handles real-time updates)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [task?.status, resultsFilter, resultsPagination.page]);

  // Calculate speed when task updates
  useEffect(() => {
    if (!task || task.status !== 'running') {
      if (task?.status !== 'running') {
        setSpeed(0);
        setInstantSpeed(0);
        setEta('-');
        speedSamplesRef.current = [];
      }
      return;
    }

    const processed = task.success_count + task.failed_count;
    const now = Date.now();

    // Add new sample
    speedSamplesRef.current.push({ timestamp: now, processed });

    // Keep only last 30 seconds of samples for more responsive speed calculation
    const cutoff = now - 30000;
    speedSamplesRef.current = speedSamplesRef.current.filter(s => s.timestamp > cutoff);

    // Calculate speed from samples
    if (speedSamplesRef.current.length >= 2) {
      const oldest = speedSamplesRef.current[0];
      const newest = speedSamplesRef.current[speedSamplesRef.current.length - 1];
      const timeDiff = (newest.timestamp - oldest.timestamp) / 1000; // seconds
      const processedDiff = newest.processed - oldest.processed;

      if (timeDiff > 0) {
        const rawSpeed = processedDiff / timeDiff;

        // Calculate instantaneous speed from last 5 seconds
        const recentCutoff = now - 5000;
        const recentSamples = speedSamplesRef.current.filter(s => s.timestamp > recentCutoff);
        if (recentSamples.length >= 2) {
          const recentOldest = recentSamples[0];
          const recentNewest = recentSamples[recentSamples.length - 1];
          const recentTimeDiff = (recentNewest.timestamp - recentOldest.timestamp) / 1000;
          const recentProcessedDiff = recentNewest.processed - recentOldest.processed;
          if (recentTimeDiff > 0) {
            setInstantSpeed(recentProcessedDiff / recentTimeDiff);
          }
        }

        // Apply exponential moving average for smoother display
        // EMA smoothing factor (0.2 = 20% new value, 80% old value)
        const alpha = 0.2;
        const smoothedSpeed = speed > 0 ? (alpha * rawSpeed + (1 - alpha) * speed) : rawSpeed;
        setSpeed(smoothedSpeed);

        // Calculate ETA using smoothed speed
        if (smoothedSpeed > 0 && task.pending_count > 0) {
          const secondsRemaining = task.pending_count / smoothedSpeed;
          setEta(formatDuration(secondsRemaining));
        } else if (task.pending_count === 0) {
          setEta('Done');
        } else {
          setEta('Calculating...');
        }
      }
    } else {
      setEta('Calculating...');
    }
  }, [task?.success_count, task?.failed_count, task?.status]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${mins}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    }
  };

  const fetchTask = async () => {
    try {
      const response = await axios.get(`${API_URL}/captioning-tasks/${id}`);
      setTask(response.data);
    } catch (error) {
      console.error('Failed to fetch task:', error);
      message.error('Failed to load task');
      navigate('/captioning');
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (page = 1, status?: string, showLoading = true) => {
    if (showLoading) setResultsLoading(true);
    try {
      const params: any = { page, limit: resultsPagination.pageSize };
      // Use provided status, or fall back to current filter state
      const filterStatus = status !== undefined ? status : resultsFilter;
      if (filterStatus) params.status = filterStatus;

      const response = await axios.get(`${API_URL}/captioning-tasks/${id}/results`, { params });
      setResults(response.data.results);
      setResultsPagination({
        page: response.data.pagination.page,
        total: response.data.pagination.total,
        pageSize: response.data.pagination.limit,
      });
    } catch (error) {
      console.error('Failed to fetch results:', error);
    } finally {
      if (showLoading) setResultsLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      // Reset speed tracking when starting
      speedSamplesRef.current = [];
      setSpeed(0);
      setEta('Calculating...');

      await axios.post(`${API_URL}/captioning-tasks/${id}/start`);
      message.success('Task started');
      fetchTask();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to start task');
    }
  };

  const handlePause = async () => {
    console.log('[Pause] Attempting to pause task:', id);
    try {
      const response = await axios.post(`${API_URL}/captioning-tasks/${id}/pause`);
      console.log('[Pause] Response:', response.data);
      message.success('Task paused');
      fetchTask();
    } catch (error: any) {
      console.error('[Pause] Error:', error);
      message.error(error.response?.data?.error || 'Failed to pause task');
    }
  };

  const handleRetryFailed = async () => {
    try {
      // Reset speed tracking when starting
      speedSamplesRef.current = [];
      setSpeed(0);
      setEta('Calculating...');

      await axios.post(`${API_URL}/captioning-tasks/${id}/start`, { retryFailed: true });
      message.success('Retrying failed images');
      fetchTask();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to retry failed images');
    }
  };

  const handleSaveBatchSize = async () => {
    if (!task || tempBatchSize === task.batch_size) {
      setEditingBatchSize(false);
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/captioning-tasks/${id}`, { batch_size: tempBatchSize });
      message.success('Batch size updated');
      setEditingBatchSize(false);
      fetchTask();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRequestsPerSecond = async () => {
    if (!task || tempRequestsPerSecond === task.requests_per_second) {
      setEditingRequestsPerSecond(false);
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/captioning-tasks/${id}`, { requests_per_second: tempRequestsPerSecond });
      message.success('Requests per second updated');
      setEditingRequestsPerSecond(false);
      fetchTask();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleEditFilters = async () => {
    if (!task) return;

    // Fetch available options from dataset
    try {
      const [sizeRes, aspectRes] = await Promise.all([
        axios.get(`${API_URL}/datasets/${task.dataset_id}/size-stats`),
        axios.get(`${API_URL}/datasets/${task.dataset_id}/aspect-ratio-stats`),
      ]);

      setSizeOptions(
        Object.entries(sizeRes.data).map(([key, count]) => ({
          label: `${key} (${(count as number).toLocaleString()})`,
          value: key,
        }))
      );

      setAspectRatioOptions(
        Object.entries(aspectRes.data)
          .sort((a, b) => (b[1] as number) - (a[1] as number))
          .map(([key, count]) => ({
            label: `${key} (${(count as number).toLocaleString()})`,
            value: key,
          }))
      );

      setTempSizeFilter(task.size_filter || []);
      setTempAspectRatioFilter(task.aspect_ratio_filter || []);
      setEditingFilters(true);
    } catch (error) {
      message.error('Failed to load filter options');
    }
  };

  const handleSaveFilters = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/captioning-tasks/${id}`, {
        size_filter: tempSizeFilter.length > 0 ? tempSizeFilter : null,
        aspect_ratio_filter: tempAspectRatioFilter.length > 0 ? tempAspectRatioFilter : null,
      });
      message.success('Filters updated');
      setEditingFilters(false);
      fetchTask();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const openLocalFile = async (filePath: string) => {
    try {
      await axios.post(`${API_URL}/open-file`, { filePath });
    } catch (error: any) {
      message.error('Failed to open file');
    }
  };

  const getStatusTag = (status: string) => {
    const config: { [key: string]: { color: string; icon: React.ReactNode } } = {
      pending: { color: 'default', icon: <ClockCircleOutlined /> },
      running: { color: 'processing', icon: <SyncOutlined spin /> },
      paused: { color: 'warning', icon: <PauseCircleOutlined /> },
      completed: { color: 'success', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', icon: <CloseCircleOutlined /> },
    };
    const { color, icon } = config[status] || { color: 'default', icon: null };
    return <Tag color={color} icon={icon}>{status.toUpperCase()}</Tag>;
  };

  const getProgress = () => {
    if (!task || task.total_images === 0) return 0;
    return Math.round(((task.success_count + task.failed_count) / task.total_images) * 100);
  };

  const getThumbnailUrl = (result: CaptionResult) => result.thumbnail_uri || result.s3_url || '';

  const resultColumns = [
    {
      title: 'Image',
      key: 'image',
      width: 80,
      render: (_: any, record: CaptionResult) => (
        <img
          src={getThumbnailUrl(record)}
          alt={`Image ${record.image_id}`}
          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
        />
      ),
    },
    {
      title: 'Image ID',
      dataIndex: 'image_id',
      key: 'image_id',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: { [key: string]: string } = {
          pending: 'default',
          processing: 'processing',
          success: 'success',
          failed: 'error',
        };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Output File',
      dataIndex: 'json_file_path',
      key: 'json_file_path',
      render: (filePath: string | null) => filePath ? (
        <Button
          type="link"
          size="small"
          onClick={() => openLocalFile(filePath)}
          style={{ padding: 0 }}
        >
          <Text code>{filePath.split('/').pop()}</Text>
        </Button>
      ) : '-',
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (error: string | null) => error ? <Text type="danger">{error}</Text> : '-',
    },
  ];

  if (loading || !task) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/captioning')}
              style={{ marginBottom: '8px', marginLeft: '-8px' }}
            >
              Back to Tasks
            </Button>
            <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <RobotOutlined style={{ color: '#6366f1' }} />
              {task.name}
              {getStatusTag(task.status)}
            </Title>
          </div>

          <Space>
            {(task.status === 'pending' || task.status === 'paused') && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleStart}
                size="large"
              >
                {task.status === 'paused' ? 'Resume' : 'Start'}
              </Button>
            )}
            {task.status === 'paused' && task.failed_count > 0 && (
              <Button
                icon={<SyncOutlined />}
                onClick={handleRetryFailed}
                size="large"
              >
                Retry Failed ({task.failed_count})
              </Button>
            )}
            {task.status === 'running' && (
              <Button
                icon={<PauseCircleOutlined />}
                onClick={handlePause}
                size="large"
              >
                Pause
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* Progress */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={24} align="middle">
          <Col flex="1">
            <Progress
              percent={getProgress()}
              status={task.status === 'failed' ? 'exception' : task.status === 'running' ? 'active' : undefined}
              strokeColor={{
                '0%': '#6366f1',
                '100%': '#52c41a',
              }}
              size={['100%', 20]}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: '24px' }}>
          <Col span={3}>
            <Statistic
              title="Pending"
              value={task.pending_count}
              prefix={<ClockCircleOutlined style={{ color: '#8c8c8c' }} />}
            />
          </Col>
          <Col span={3}>
            <Statistic
              title="Success"
              value={task.success_count}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={3}>
            <Statistic
              title="Failed"
              value={task.failed_count}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Col>
          <Col span={3}>
            <Statistic
              title="Total"
              value={task.total_images}
              prefix={<RobotOutlined style={{ color: '#6366f1' }} />}
            />
          </Col>
          <Col span={3}>
            <Statistic
              title="Current Speed"
              value={instantSpeed.toFixed(1)}
              valueStyle={{
                color: task.status === 'running'
                  ? (instantSpeed >= TARGET_SPEED ? '#52c41a' : instantSpeed >= TARGET_SPEED * 0.7 ? '#faad14' : '#ff4d4f')
                  : '#8c8c8c'
              }}
              prefix={<ThunderboltOutlined />}
            />
          </Col>
          <Col span={3}>
            <Statistic
              title="Avg Speed (30s)"
              value={speed.toFixed(1)}
              valueStyle={{
                color: task.status === 'running'
                  ? (speed >= TARGET_SPEED ? '#52c41a' : speed >= TARGET_SPEED * 0.7 ? '#faad14' : '#ff4d4f')
                  : '#8c8c8c'
              }}
              prefix={<ThunderboltOutlined />}
            />
          </Col>
          <Col span={3}>
            <Statistic
              title="API Utilization"
              value={task.status === 'running' ? ((speed / MAX_THEORETICAL_SPEED) * 100).toFixed(0) : 0}
              suffix="%"
              valueStyle={{
                color: task.status === 'running'
                  ? (speed / MAX_THEORETICAL_SPEED >= 0.89 ? '#52c41a' : speed / MAX_THEORETICAL_SPEED >= 0.6 ? '#faad14' : '#ff4d4f')
                  : '#8c8c8c'
              }}
            />
          </Col>
          <Col span={3}>
            <Statistic
              title="ETA"
              value={eta}
              valueStyle={{ color: task.status === 'running' ? '#722ed1' : '#8c8c8c' }}
              prefix={<FieldTimeOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* Task Details */}
      <Card title="Task Details" style={{ marginBottom: '16px' }}>
        <Descriptions column={2}>
          <Descriptions.Item label="Dataset">{task.dataset_name}</Descriptions.Item>
          <Descriptions.Item label="Model">
            <Tag color="blue">{task.model}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Prompt">{task.prompt_name} (v{task.prompt_version})</Descriptions.Item>
          <Descriptions.Item label="Provider">
            <Tag>{task.provider.toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Batch Size">
            {task.status === 'paused' && editingBatchSize ? (
              <Space>
                <InputNumber
                  min={1}
                  value={tempBatchSize}
                  onChange={(val) => setTempBatchSize(val || 1)}
                  style={{ width: 80 }}
                  size="small"
                />
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={handleSaveBatchSize}
                  loading={saving}
                />
                <Button
                  size="small"
                  onClick={() => setEditingBatchSize(false)}
                >
                  Cancel
                </Button>
              </Space>
            ) : (
              <Space>
                {task.batch_size}
                {task.status === 'paused' && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setTempBatchSize(task.batch_size);
                      setEditingBatchSize(true);
                    }}
                    style={{ padding: 0 }}
                  />
                )}
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Requests/Second (per worker)">
            {task.status === 'paused' && editingRequestsPerSecond ? (
              <Space>
                <InputNumber
                  min={1}
                  max={20}
                  value={tempRequestsPerSecond}
                  onChange={(val) => setTempRequestsPerSecond(val || 1)}
                  style={{ width: 80 }}
                  size="small"
                />
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={handleSaveRequestsPerSecond}
                  loading={saving}
                />
                <Button
                  size="small"
                  onClick={() => setEditingRequestsPerSecond(false)}
                >
                  Cancel
                </Button>
              </Space>
            ) : (
              <Space>
                {task.requests_per_second || 6} req/s
                {task.status === 'paused' && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setTempRequestsPerSecond(task.requests_per_second || 6);
                      setEditingRequestsPerSecond(true);
                    }}
                    style={{ padding: 0 }}
                  />
                )}
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Size Filter" span={2}>
            {task.status === 'paused' && editingFilters ? (
              <Select
                mode="multiple"
                allowClear
                placeholder="All sizes"
                value={tempSizeFilter}
                onChange={setTempSizeFilter}
                options={sizeOptions}
                style={{ width: '100%' }}
                size="small"
              />
            ) : (
              <Space wrap>
                {task.size_filter && task.size_filter.length > 0 ? (
                  task.size_filter.map(s => <Tag key={s} color="blue">{s}</Tag>)
                ) : (
                  <Text type="secondary">All sizes</Text>
                )}
                {task.status === 'paused' && !editingFilters && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={handleEditFilters}
                    style={{ padding: 0 }}
                  />
                )}
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Aspect Ratio Filter" span={2}>
            {task.status === 'paused' && editingFilters ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All ratios"
                  value={tempAspectRatioFilter}
                  onChange={setTempAspectRatioFilter}
                  options={aspectRatioOptions}
                  style={{ width: '100%' }}
                  size="small"
                />
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={handleSaveFilters}
                    loading={saving}
                  >
                    Save Filters
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setEditingFilters(false)}
                  >
                    Cancel
                  </Button>
                </Space>
              </Space>
            ) : (
              <Space wrap>
                {task.aspect_ratio_filter && task.aspect_ratio_filter.length > 0 ? (
                  task.aspect_ratio_filter.map(r => <Tag key={r} color="purple">{r}</Tag>)
                ) : (
                  <Text type="secondary">All ratios</Text>
                )}
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Created">{new Date(task.created_at).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Started">
            {task.started_at ? new Date(task.started_at).toLocaleString() : '-'}
          </Descriptions.Item>
          {task.completed_at && (
            <Descriptions.Item label="Completed">
              {new Date(task.completed_at).toLocaleString()}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Results */}
      <Card title="Caption Results">
        <Tabs
          defaultActiveKey="all"
          onChange={(key) => {
            const status = key === 'all' ? undefined : key;
            setResultsFilter(status);
            fetchResults(1, status);
          }}
          items={[
            {
              key: 'all',
              label: (
                <span>
                  All <Badge count={task.success_count + task.failed_count} style={{ backgroundColor: '#6366f1' }} />
                </span>
              ),
            },
            {
              key: 'success',
              label: (
                <span>
                  Success <Badge count={task.success_count} style={{ backgroundColor: '#52c41a' }} />
                </span>
              ),
            },
            {
              key: 'failed',
              label: (
                <span>
                  Failed <Badge count={task.failed_count} style={{ backgroundColor: '#ff4d4f' }} />
                </span>
              ),
            },
          ]}
        />

        <Table
          columns={resultColumns}
          dataSource={results}
          rowKey="id"
          loading={resultsLoading}
          pagination={{
            current: resultsPagination.page,
            total: resultsPagination.total,
            pageSize: resultsPagination.pageSize,
            onChange: (page) => fetchResults(page, resultsFilter),
          }}
        />
      </Card>
    </div>
  );
};

export default CaptioningTaskView;
